import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star } from "lucide-react";

export function AboutSocialProofSection() {
  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Daily Commuter",
      content: "I’ve stuck Metefy QR codes on my metro card and charger. The setup took less than 2 minutes, and it’s a huge relief knowing that if I ever misplace something, there’s at least a chance it can make its way back to me.",
      avatar: "https://api.dicebear.com/9.x/adventurer/svg?seed=Priya",
      rating: 5
    },
    {
      name: "Rajesh Kumar",
      role: "Tech Professional",
      content: "The idea is so simple, but the value is massive. I’m always moving between cafés, coworking spaces, and the metro — and Metefy just adds this quiet layer of security to the things I carry every day.",
      avatar: "https://api.dicebear.com/9.x/adventurer/svg?seed=Rajesh",
      rating: 5
    },
    {
      name: "Anita Patel",
      role: "Student",
      content: "Everything was super easy to activate and personalize. I added my Twitter and a short message in my profile, and I love how non-intrusive the stickers are. Can’t wait to see how many people this ends up helping.",
      avatar: "https://api.dicebear.com/9.x/adventurer/svg?seed=Anita",
      rating: 5
    }
  ];

  // Generate Review Schema for SEO
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Metefy - Lost Item Recovery Service",
    "image": "https://metefy.com/logo.svg",
    "description": "Metefy provides a digital identity for your valuables, enabling quick and secure recovery of lost items through a peer-to-peer platform.",
    "brand": {
      "@type": "Brand",
      "name": "Metefy"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": (testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1),
      "reviewCount": testimonials.length.toString(),
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": testimonials.map(testimonial => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": testimonial.rating.toString(),
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": testimonial.name
      },
      "datePublished": "2024-01-01",
      "reviewBody": testimonial.content,
      "publisher": { "@type": "Organization", "name": "Metefy" }
    }))
  };

  return (
    <section id="reviews" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <Star className="w-4 h-4 text-foreground" />
            <span className="text-foreground">User Stories</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Loved by Our <span className="text-primary">Community</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
          Real experiences from early users who trust Metefy with their valuables
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-card border-2 border-border hover:border-primary hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-foreground/80 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <Avatar className="border-2 border-border">
                    <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                    <AvatarFallback className="bg-muted text-foreground">{testimonial.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-foreground/80 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Inject Review Schema for SEO */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewSchema) }}
        aria-hidden="true"
      />
    </section>
  );
} 